<template>
  <e-dialog ref="dialogRef" @after-leave="removeImage">
    <template #title>
      <div class="caption">Фото профиля</div>
    </template>
    <template #activator>
      <e-btn variant="link">
        <template #prepend>
          <e-icon name="gallery" />
        </template>
        Фото
      </e-btn>
    </template>

    <e-file-load-area
      v-if="!sourceImg"
      :accept="imageExtensions"
      class="user-avatar__file-load-area"
      :max-size="getConfigValue('maxUploadFileSize')"
      :validation="checkFileIsImage"
      @upload="onFileUpload"
    />

    <template v-else>
      <vue-cropper
        ref="cropperRef"
        :aspect-ratio="1"
        :view-mode="1"
        :min-crop-box-width="100"
        :src="sourceImg"
        :container-style="{ aspectRatio: 1 }"
        class="cropper"
        @cropend="setHasChanges"
      />

      <div class="cropper__btns">
        <e-btn variant="ghost" icon class="mr-1" @click="reflect">
          <e-icon name="reflect" />
        </e-btn>
        <e-btn variant="ghost" icon class="mr-1" @click="rotate">
          <e-icon name="rotateLeft" />
        </e-btn>
        <e-btn variant="ghost" :disabled="!hasChanges" @click="reset"> Сброс</e-btn>
        <template v-if="!$vuetify.display.xs">
          <v-spacer />
          <e-btn variant="secondary" class="mr-4" @click="cancel"> Отмена</e-btn>
          <e-btn :loading="isLoading" variant="primary" @click="save"> Готово</e-btn>
        </template>
      </div>
    </template>

    <template v-if="sourceImg && $vuetify.display.xs" #buttons>
      <e-btn variant="secondary" class="cropper__btns-action" @click="cancel"> Отмена</e-btn>
      <e-btn :loading="isLoading" variant="primary" class="cropper__btns-action" @click="save"> Готово</e-btn>
    </template>
  </e-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import VueCropper from 'vue-cropperjs';
import type { IDialog } from '@/core/types/components/dialog';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EBtn, EFileLoadArea, EIcon } from '@/core/components/ui';
import { useServices } from '@/core/composables/services';
import { getConfigValue } from '@/core/consts/config';
import 'cropperjs/dist/cropper.css';
import { checkFileIsImage, getImageLocalUrl, imageExtensions } from '@/core/utils/file';
import { AddUserAvatar } from '@/parts/auth/gql/user';
import { useAuthStore } from '@/parts/auth/store/auth';

const { $api } = useServices();
const { getProfile } = useAuthStore();

const sourceImg = ref();
const sourceImgType = ref();
const cropperRef = ref();

const onFileUpload = (files: File[]) => {
  const file = files[0];

  if (!file) {
    return;
  }

  sourceImg.value = getImageLocalUrl(file);
  sourceImgType.value = file.type;
};

const isLoading = ref(false);

const dialogRef = ref<IDialog>();

const cancel = () => {
  dialogRef.value?.close();
};

const save = async () => {
  if (!cropperRef.value) {
    return;
  }

  isLoading.value = true;

  try {
    const croppedImg = await new Promise((resolve) => {
      cropperRef.value.getCroppedCanvas().toBlob((blob: Blob) => {
        resolve(blob);
      }, sourceImgType.value);
    });

    const response = await $api.mutation(AddUserAvatar, {
      image: croppedImg,
    });

    if (!response?.errors) {
      await getProfile(true);

      cancel();
    }
  } finally {
    isLoading.value = false;
  }
};

const removeImage = () => {
  sourceImg.value = undefined;
};

const hasChanges = ref(false);

const setHasChanges = () => {
  hasChanges.value = true;
};

const reset = () => {
  if (!cropperRef.value) {
    return;
  }

  cropperRef.value.reset();

  hasChanges.value = false;
};

const reflect = () => {
  if (!cropperRef.value) {
    return;
  }

  const data = cropperRef.value.getData();

  cropperRef.value.scaleX(data.scaleX === -1 ? 1 : -1);

  setHasChanges();
};

const rotate = () => {
  if (!cropperRef.value) {
    return;
  }

  const data = cropperRef.value.getData();

  cropperRef.value.rotateTo(data.rotate - 90);

  setHasChanges();
};
</script>

<style scoped lang="scss">
.cropper {
  max-height: 640px;

  @include sm-and-down {
    max-height: 400px;
  }

  :deep(.cropper-container) {
    .cropper-wrap-box {
      background-color: $black-color;
    }

    .dashed-h,
    .dashed-v {
      display: none;
    }

    .cropper-view-box {
      border-radius: 50%;
      outline: none;
    }

    .cropper-face,
    .cropper-line,
    .cropper-point {
      background-color: transparent;
    }

    .cropper-point {
      width: 24px;
      height: 24px;
    }

    .point-se,
    .point-sw,
    .point-nw,
    .point-ne {
      border: 2px solid $white-color;
    }

    .cropper-center {
      display: none;
    }

    .point-se {
      border-width: 0 2px 2px 0;
    }

    .point-sw {
      border-width: 0 0 2px 2px;
    }

    .point-nw {
      border-width: 2px 0 0 2px;
    }

    .point-ne {
      border-width: 2px 2px 0 0;
    }
  }

  &__btns {
    display: flex;

    margin-top: 24px;

    @include extra-xs {
      justify-content: center;
    }
  }
}

.user-avatar__file-load-area {
  aspect-ratio: 1;

  @include md-and-down {
    max-height: 60vh;
  }
}
</style>
